import type { Measured } from "../contracts";
import { comparableNumber, recordedToolCalls } from "../metrics";

function startMs(session: { firstActivityAt?: string | null }): number | null {
  if (!session.firstActivityAt) return null;
  const t = Date.parse(session.firstActivityAt);
  return Number.isFinite(t) ? t : null;
}

type SessionUsage = {
  firstActivityAt?: string | null;
  tokens: { total: Measured<number> };
  toolCalls: Measured<number>;
  nonzeroShellExits: Measured<number>;
  knownShellExits?: Measured<number>;
  modelBucket?: string;
};

export type ActivityMetric = "sessions" | "tokens" | "tokensPerSession" | "toolCalls" | "callsPerSession" | "errors" | "errorShare";

export type ActivityBin = {
  t: number;
  sessions: number;
  tokens: number;
  tokensKnown: number;
  toolCalls: number;
  toolCallsKnown: number;
  errors: number;
  errorsKnown: number;
};

/** One UTC-week (Monday) bin of normalized agent burn. Medians over known sessions only. */
export type BurnBin = {
  t: number;
  sessions: number;
  medianTokens: number | null;
  tokensKnown: number;
  medianToolCalls: number | null;
  toolCallsKnown: number;
  errorShare: number | null;
  exitsKnown: number;
};

export type StoryChange = {
  id: string;
  title: string;
  occurredAt: string;
  category?: string;
  commitSha?: string | null;
  startAt?: string | null;
  endAt?: string | null;
};

export type ScanPair = { start: number; end: number };

/** One observational line. Association in time, not proof a git change caused the code to move. */
export const HONESTY_LINE = "Aligned in time. Not proof this git change caused the delta.";

export const ACTIVITY_METRICS: { id: ActivityMetric; label: string }[] = [
  { id: "sessions", label: "Session starts" },
  { id: "tokens", label: "Tokens" },
  { id: "toolCalls", label: "Tool calls" },
  { id: "errors", label: "Shell errors" },
  { id: "tokensPerSession", label: "Tokens/session" },
  { id: "callsPerSession", label: "Calls/session" },
  { id: "errorShare", label: "Error share" },
];

export const BURN_METRICS: ReadonlySet<ActivityMetric> = new Set(["tokensPerSession", "callsPerSession", "errorShare"]);

/** Bin policy, shown next to the bars so the viewport never implies more than it measures. */
export const ACTIVITY_BIN_POLICY =
  "UTC-day bins for totals, UTC-week (Monday) bins for per-session medians. Each session counts once, on the day/week it started. Child-family sessions and sessions without a recorded start are excluded from the bars and counted alongside.";

export type ActivityScope = {
  included: number;
  childSessions: number;
  timelessSessions: number;
  unknownActorSessions: number;
};

/** A dated chat record alone is not evidence of agent work. Keep its record, but
 * do not let metadata-only sessions create work bars or stretch the chart. */
export function hasRecordedAgentUsage(session: SessionUsage): boolean {
  const tokens=session.tokens.total, calls=session.toolCalls;
  return (tokens.availability!=="unavailable" && tokens.value!=null)
    || (calls.availability!=="unavailable" && calls.value!=null && calls.value>0);
}

/** Who is in the bars: primary and unknown-actor sessions with a recorded start. Children never mix into family totals. */
export function summarizeActivityScope(
  sessions: { actorKind?: string | null; firstActivityAt?: string | null }[],
): ActivityScope {
  let included = 0, childSessions = 0, timelessSessions = 0, unknownActorSessions = 0;
  for (const session of sessions) {
    if (session.actorKind === "child") { childSessions += 1; continue; }
    if (startMs(session) == null) { timelessSessions += 1; continue; }
    included += 1;
    if (session.actorKind === "unknown") unknownActorSessions += 1;
  }
  return { included, childSessions, timelessSessions, unknownActorSessions };
}

export function utcDay(ms: number): number {
  const date = new Date(ms);
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

/** Monday 00:00 UTC of the week containing ms. */
export function utcWeek(ms: number): number {
  const day = utcDay(ms);
  const dow = new Date(day).getUTCDay();
  return day - ((dow + 6) % 7) * 86400000;
}

/** Median of known values; null when nothing is known. Never zero-filled. */
export function median(values: number[]): number | null {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
}

/** First-to-last trusted move in median tokens/session. Null when fewer than two known weeks. */
export function burnTrend(bins: BurnBin[]): { from: BurnBin; to: BurnBin; changePct: number | null } | null {
  const known = bins.filter((b) => b.tokensKnown > 0 && b.medianTokens != null);
  if (known.length < 2) return null;
  const from = known[0]!, to = known[known.length - 1]!;
  if (!from.medianTokens) return null;
  return { from, to, changePct: (to.medianTokens! - from.medianTokens) / from.medianTokens };
}

export type BurnWindow = { median: number; known: number; label: string };

/**
 * Pooled 4-week vs prior-4-week median tokens/session. Single-session weeks never
 * anchor a trend claim: each side needs minSessions known sessions or there is no
 * comparison, only the latest qualified week.
 */
export function burnComparison(
  sessions: SessionUsage[],
  now = Date.now(),
  minSessions = 5,
  windowWeeks = 4,
): { recent: BurnWindow; previous: BurnWindow; changePct: number } | null {
  const span = windowWeeks * 7 * 86400000;
  const weekStart = utcWeek(now) - (windowWeeks - 1) * 7 * 86400000;
  const recentCut = weekStart;
  const previousCut = weekStart - span;
  const recentValues: number[] = [];
  const previousValues: number[] = [];
  for (const session of sessions) {
    const start = startMs(session);
    if (start == null || start > now) continue;
    const value = comparableNumber(session.tokens.total);
    if (value == null) continue;
    if (start >= recentCut) recentValues.push(value);
    else if (start >= previousCut) previousValues.push(value);
  }
  if (recentValues.length < minSessions || previousValues.length < minSessions) return null;
  const recentMedian = median(recentValues)!;
  const previousMedian = median(previousValues)!;
  if (!previousMedian) return null;
  const label = (t: number) => new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return {
    recent: { median: recentMedian, known: recentValues.length, label: `${label(recentCut)} → now` },
    previous: { median: previousMedian, known: previousValues.length, label: `${label(previousCut)} → ${label(recentCut)}` },
    changePct: (recentMedian - previousMedian) / previousMedian,
  };
}

/** Weekly normalized burn: per-session medians plus the share of sessions with shell errors. */
export function burnBins(sessions: SessionUsage[]): BurnBin[] {
  const weeks = new Map<number, { tokens: number[]; tools: number[]; friction: number; known: number; sessions: number }>();
  for (const session of sessions) {
    const start = startMs(session);
    if (start == null) continue;
    const week = utcWeek(start);
    const row = weeks.get(week) ?? { tokens: [], tools: [], friction: 0, known: 0, sessions: 0 };
    row.sessions += 1;
    const tokens = comparableNumber(session.tokens.total);
    if (tokens != null) row.tokens.push(tokens);
    const tools = recordedToolCalls(session.toolCalls);
    if (tools != null) row.tools.push(tools);
    const errors = comparableNumber(session.nonzeroShellExits);
    if (errors != null) {
      row.known += 1;
      if (errors > 0) row.friction += 1;
    }
    weeks.set(week, row);
  }
  return [...weeks.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([t, row]) => ({
      t,
      sessions: row.sessions,
      medianTokens: median(row.tokens),
      tokensKnown: row.tokens.length,
      medianToolCalls: median(row.tools),
      toolCallsKnown: row.tools.length,
      errorShare: row.known ? row.friction / row.known : null,
      exitsKnown: row.known,
    }));
}

export function activityBins(sessions: SessionUsage[]): ActivityBin[] {
  const days = new Map<number, ActivityBin>();
  for (const session of sessions) {
    const start = startMs(session);
    if (start == null) continue;
    const day = utcDay(start);
    const row = days.get(day) ?? { t: day, sessions: 0, tokens: 0, tokensKnown: 0, toolCalls: 0, toolCallsKnown: 0, errors: 0, errorsKnown: 0 };
    row.sessions += 1;
    const tokens = comparableNumber(session.tokens.total);
    if (tokens != null) { row.tokens += tokens; row.tokensKnown += 1; }
    const tools = recordedToolCalls(session.toolCalls);
    if (tools != null) { row.toolCalls += tools; row.toolCallsKnown += 1; }
    const errors = comparableNumber(session.nonzeroShellExits);
    if (errors != null) { row.errors += errors; row.errorsKnown += 1; }
    days.set(day, row);
  }
  return [...days.values()].sort((a, b) => a.t - b.t);
}

export function changeWindow(
  change: StoryChange,
  fallbackDays = 3,
): { start: number; end: number; kind: "phase" | "point" } {
  if (change.startAt && change.endAt) {
    const start = Date.parse(change.startAt);
    const end = Date.parse(change.endAt);
    if (Number.isFinite(start) && Number.isFinite(end) && end >= start) {
      return { start, end, kind: "phase" };
    }
  }
  const t = Date.parse(change.occurredAt);
  const pad = fallbackDays * 86400000;
  return { start: t - pad, end: t + pad, kind: "point" };
}

/** Agent-usage span for a git point marker: last scan before the commit through first scan after it. */
export function scanPairWindow(
  beforeMs: number | null,
  afterMs: number | null,
): ScanPair | null {
  if (beforeMs == null || afterMs == null) return null;
  if (!Number.isFinite(beforeMs) || !Number.isFinite(afterMs) || afterMs < beforeMs) return null;
  return { start: beforeMs, end: afterMs };
}

type PairPoint = { t: number; commitSha?: string | null };

/** Last measured point at or before the marker, and first after it.
 *  Config detections are stamped at scan time, which can sit after the commit's chart time —
 *  if there is no later scan, the snapshot of that commit is the after point. */
export function pickScanPair<T extends PairPoint>(
  points: T[],
  marker: { occurredAt: string; startAt?: string | null; endAt?: string | null; commitSha?: string | null },
): { before: T | null; after: T | null } {
  if (marker.startAt && marker.endAt) {
    const start = Date.parse(marker.startAt), end = Date.parse(marker.endAt);
    if (Number.isFinite(start) && Number.isFinite(end) && end >= start) {
      const sorted = [...points].sort((a, b) => a.t - b.t);
      const before = [...sorted].reverse().find((point) => point.t <= start) ?? null;
      const after = sorted.find((point) => point.t >= end && point !== before) ?? null;
      return { before, after };
    }
  }
  const t = Date.parse(marker.occurredAt);
  if (!Number.isFinite(t)) return { before: null, after: null };
  const sorted = [...points].sort((a, b) => a.t - b.t);
  let before = [...sorted].reverse().find((point) => point.t <= t) ?? null;
  let after = sorted.find((point) => point.t > t) ?? null;
  const sha = marker.commitSha?.toLowerCase() ?? "";
  if (!after && sha) {
    const atCommit = [...sorted].reverse().find((point) => (point.commitSha ?? "").toLowerCase() === sha) ?? null;
    if (atCommit) {
      after = atCommit;
      if (!before || before === atCommit || before.t >= atCommit.t) {
        before = [...sorted].reverse().find((point) => point.t < atCommit.t) ?? null;
      }
    }
  }
  if (before && after && before === after) {
    before = [...sorted].reverse().find((point) => point.t < after!.t) ?? null;
  }
  return { before, after };
}

export function scanPairFromPoints(
  points: PairPoint[],
  marker: { occurredAt: string; startAt?: string | null; endAt?: string | null; commitSha?: string | null },
): ScanPair | null {
  if (marker.startAt && marker.endAt) {
    const start = Date.parse(marker.startAt), end = Date.parse(marker.endAt);
    if (Number.isFinite(start) && Number.isFinite(end) && end >= start) return { start, end };
  }
  const t = Date.parse(marker.occurredAt);
  if (!Number.isFinite(t)) return null;
  const { before, after } = pickScanPair(points, marker);
  const complete = scanPairWindow(before?.t ?? null, after?.t ?? null);
  if (complete) return complete;
  if (before && t >= before.t) return { start: before.t, end: t };
  if (after && t <= after.t) return { start: t, end: after.t };
  return { start: t, end: t };
}

/** Sessions for an investigation: recorded phase, else last-before / first-after, else a partial side. Never a calendar pad. */
export function sessionJoinWindow(input: {
  startAt?: string | null;
  endAt?: string | null;
  beforeMs: number | null;
  afterMs: number | null;
  markerMs: number;
}): { start: number; end: number; kind: "phase" | "scan-pair" | "partial" } {
  if (input.startAt && input.endAt) {
    const start = Date.parse(input.startAt), end = Date.parse(input.endAt);
    if (Number.isFinite(start) && Number.isFinite(end) && end >= start) return { start, end, kind: "phase" };
  }
  const pair = scanPairWindow(input.beforeMs, input.afterMs);
  if (pair) return { ...pair, kind: "scan-pair" };
  const start = input.beforeMs ?? input.markerMs;
  const end = input.afterMs ?? input.markerMs;
  const a = Number.isFinite(start) ? start : input.markerMs;
  const b = Number.isFinite(end) ? end : input.markerMs;
  return { start: Math.min(a, b), end: Math.max(a, b), kind: "partial" };
}

export function scanPairFocus(
  times: number[],
  pair: ScanPair | null,
  occurredAt?: number | null,
): { start: number; end: number } {
  const extra = occurredAt != null && Number.isFinite(occurredAt) ? [occurredAt] : [];
  if (!times.length && !extra.length) return { start: 0, end: 100 };
  const min = Math.min(...times, ...extra);
  const max = Math.max(...times, ...extra);
  const span = max - min;
  if (!(span > 0)) return { start: 0, end: 100 };
  const targetStart = pair?.start ?? occurredAt ?? null;
  const targetEnd = pair?.end ?? occurredAt ?? null;
  if (targetStart == null || targetEnd == null) return { start: 0, end: 100 };
  const neighborhood = Math.max(10 * 86400000, span * 0.12);
  const pad = Math.max((targetEnd - targetStart) * 0.25, span * 0.04, 2 * 86400000);
  let from = targetStart - pad;
  let to = targetEnd + pad;
  if (to - from > span * 0.45) {
    const center = occurredAt != null && Number.isFinite(occurredAt) ? occurredAt : (targetStart + targetEnd) / 2;
    from = center - neighborhood / 2;
    to = center + neighborhood / 2;
  } else if (to - from < neighborhood) {
    const mid = (from + to) / 2;
    from = mid - neighborhood / 2;
    to = mid + neighborhood / 2;
  }
  from = Math.max(min, from);
  to = Math.min(max, to);
  if (to <= from) {
    from = Math.max(min, max - neighborhood);
    to = max;
  }
  const start = ((from - min) / span) * 100;
  const endPct = ((to - min) / span) * 100;
  return { start: Math.max(0, start), end: Math.min(100, Math.max(endPct, start + 5)) };
}

export type ScanPairNoteCrossing = {
  path: string;
  name: string;
  line: number | null;
  appeared: boolean;
  cc: { before: number | null; after: number } | null;
  cognitive: { before: number | null; after: number } | null;
  span: { before: number | null; after: number } | null;
};

export function scanPairNote(input: {
  title: string;
  occurredAt: string;
  before: { commitSha: string | null; collectedAt: string; aggregates: Record<string, number | null> };
  after: { commitSha: string | null; collectedAt: string; aggregates: Record<string, number | null> };
  usage: { sessions: number; tokens: number | null; toolCalls: number | null };
  crossings: ScanPairNoteCrossing[];
  crossingsTotal: number;
}): string {
  const fmt = (n: number | null | undefined) => (typeof n === "number" ? new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(n) : "not measured");
  const delta = (key: string) => {
    const a = input.before.aggregates[key], b = input.after.aggregates[key];
    if (typeof a !== "number" || typeof b !== "number") return `${fmt(a)} → ${fmt(b)}`;
    const d = b - a;
    return `${fmt(a)} → ${fmt(b)} (${d > 0 ? "+" : ""}${fmt(d)})`;
  };
  const sha = (value: string | null) => value ? value.slice(0, 8) : "uncommitted";
  const crossingLine = (row: ScanPairNoteCrossing) => {
    const bits: string[] = [];
    if (row.cc) bits.push(`CC ${fmt(row.cc.before)} → ${fmt(row.cc.after)}`);
    if (row.cognitive) bits.push(`cognitive ${fmt(row.cognitive.before)} → ${fmt(row.cognitive.after)}`);
    if (row.span) bits.push(`span ${fmt(row.span.before)} → ${fmt(row.span.after)}`);
    return `- \`${row.path}:${row.line ?? "?"}\` ${row.name} — ${bits.join("; ")}${row.appeared ? " (not in the before scan)" : ""}`;
  };
  return [
    `# ${input.title}`,
    "",
    `Git change: ${input.title}`,
    `Occurred: ${input.occurredAt}`,
    `Last scan before: ${sha(input.before.commitSha)} · ${input.before.collectedAt}`,
    `First scan after: ${sha(input.after.commitSha)} · ${input.after.collectedAt}`,
    "",
    HONESTY_LINE,
    "",
    "## Measured delta",
    `- Source LOC: ${delta("sourceLoc")}`,
    `- Functions over CC 20: ${delta("ccAbove")}`,
    `- Duplicate groups: ${delta("cloneGroups")}`,
    `- Sessions between those scans: ${input.usage.sessions}`,
    `- Tokens: ${input.usage.tokens == null ? "not recorded" : fmt(input.usage.tokens)}`,
    `- Tool calls: ${input.usage.toolCalls == null ? "not recorded" : fmt(input.usage.toolCalls)}`,
    "",
    "## Functions that newly crossed a review bar",
    input.crossings.length
      ? [...input.crossings.map(crossingLine), input.crossingsTotal > input.crossings.length ? `- … ${input.crossingsTotal - input.crossings.length} more not listed` : ""].filter(Boolean).join("\n")
      : "None in this scan pair, or the two scans are not comparable.",
    "",
    "Inspect those locations. Analyzer thresholds are not automatic defects or a request to rewrite everything.",
  ].join("\n");
}

export function isPhase(change: StoryChange): boolean {
  return changeWindow(change).kind === "phase";
}

export function sessionsInWindow<T extends { firstActivityAt?: string | null }>(
  sessions: T[],
  start: number,
  end: number,
): T[] {
  return sessions.filter((session) => {
    const t = startMs(session);
    return t != null && t >= start && t <= end;
  });
}

export function usageInWindow(sessions: SessionUsage[]): {
  sessions: number;
  tokens: number | null;
  tokensKnown: number;
  toolCalls: number | null;
  toolCallsKnown: number;
  errors: number | null;
  knownExits: number | null;
  models: [string, number][];
} {
  const tokens: number[] = [];
  const tools: number[] = [];
  const errors: number[] = [];
  const known: number[] = [];
  const models = new Map<string, number>();
  for (const session of sessions) {
    pushKnown(tokens, session.tokens.total);
    const recorded = recordedToolCalls(session.toolCalls);
    if (recorded != null) tools.push(recorded);
    pushKnown(errors, session.nonzeroShellExits);
    if (session.knownShellExits) pushKnown(known, session.knownShellExits);
    const bucket = session.modelBucket ?? "unknown";
    models.set(bucket, (models.get(bucket) ?? 0) + 1);
  }
  return {
    sessions: sessions.length,
    tokens: tokens.length ? tokens.reduce((a, b) => a + b, 0) : null,
    tokensKnown: tokens.length,
    toolCalls: tools.length ? tools.reduce((a, b) => a + b, 0) : null,
    toolCallsKnown: tools.length,
    errors: errors.length ? errors.reduce((a, b) => a + b, 0) : null,
    knownExits: known.length ? known.reduce((a, b) => a + b, 0) : null,
    models: [...models.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])),
  };
}

function pushKnown(into: number[], metric: Measured<number>): void {
  const value = comparableNumber(metric);
  if (value != null) into.push(value);
}
